import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import * as JourneySelectors from '@/selectors/journeySelectors';
import type { EmailTemplateTypeModel } from '@/typings/api/emailCenter';
import {
  buildJourneyCoverageByType,
  isJourneySpecificType,
  scopedCoveredEmailTypes,
} from '@/utils/emailCenter';
import { getIntlTranslation } from '@/utils/intl';
import type { FormInstance } from 'antd';
import { useEffect, useMemo } from 'react';
import { useIntl, useSelector } from 'umi';
import { LEAD_SOURCE_TEMPLATES_ENABLED } from '../../constants';

interface Options {
  form: FormInstance;
  templateTypes: EmailTemplateTypeModel[];
  /** The event the drawer was opened for; `''` is marketing, `null` unknown. */
  presetEmailType: string | null;
  /** The event of the record being edited, so its own type stays selectable. */
  recordEmailType?: string | null;
  isEdit: boolean;
  creatingForTrigger: boolean;
  visible: boolean;
}

/**
 * Who this email is for — the event it answers, and the lead source it goes out
 * on — together with the coverage rules that decide what is still free to pick.
 *
 * One hook rather than four scattered memos because they are a single question
 * asked in three places: the Event select disables covered events, the create
 * step's dropdown disables covered lead sources, and the save falls back to
 * `scopeTarget` when the form named neither.
 */
export function useTemplateScopeTarget({
  form,
  templateTypes,
  presetEmailType,
  recordEmailType,
  isEdit,
  creatingForTrigger,
  visible,
}: Options) {
  const intl = useIntl();
  const transactionalTemplates = useSelector(
    EmailCenterSelectors.selectTransactionalTemplates,
  );

  /**
   * Events that already send an email stay visible but unselectable, so the
   * list still shows what exists; the record's own event remains selectable.
   *
   * "Already sends" means a journey link (journey_email_templates), not a row
   * in the pool: the pool includes the public masters every partner can see,
   * and an unlinked master sends nothing — it must not stand in the way of
   * configuring the event.
   *
   * Read for the lead source the page has selected — its own links plus the
   * default's — so this select closes off exactly what the tab's create menu
   * closes off. Judged across every journey it refused events the create menu
   * had just offered, leaving the drawer's own preselection unselectable.
   */
  const scopedJourneyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );
  const coveredEventTypes = useMemo(() => {
    const covered = scopedCoveredEmailTypes(
      transactionalTemplates,
      scopedJourneyHashId,
    );
    if (recordEmailType) covered.delete(recordEmailType);
    return covered;
  }, [transactionalTemplates, scopedJourneyHashId, recordEmailType]);

  /**
   * The scope step's data: which lead sources already send this event.
   *
   * *Every* lead source, not just journey-type ones — CSV imports, manual
   * entries and webhooks create customer projects that trigger these emails
   * too, so they can carry their own template. Coverage comes from the
   * journey usage the template list embeds, so a source that already has
   * this email type is offered disabled (visible, with the reason) rather
   * than hidden — the list still shows what exists, same rule as the event
   * select above.
   */
  const journeys = useSelector(JourneySelectors.selectJourneys);
  const defaultJourney = useMemo(
    () => journeys.find((journey) => journey.is_default) ?? null,
    [journeys],
  );
  const coveredJourneyIds = useMemo(() => {
    if (!presetEmailType) return new Set<string>();
    return (
      buildJourneyCoverageByType(transactionalTemplates).get(presetEmailType) ??
      new Set<string>()
    );
  }, [transactionalTemplates, presetEmailType]);
  const defaultScopeCovered =
    defaultJourney != null && coveredJourneyIds.has(defaultJourney.hashId);

  /**
   * One dropdown answers "when does this email go out": the default journey
   * first (what every lead source without its own email inherits), then each
   * lead source. Covered entries stay visible but disabled, with the reason.
   */
  const sendTargetOptions = useMemo(() => {
    const coveredSuffix = getIntlTranslation(intl, {
      id: 'pages.emailCenter.templates.create.leadSourceCovered',
      defaultMessage: 'already has this email',
    });
    const options = journeys
      .filter((journey) => !journey.is_default)
      .map((journey) => ({
        value: journey.hashId,
        label: coveredJourneyIds.has(journey.hashId)
          ? `${journey.name} — ${coveredSuffix}`
          : journey.name,
        disabled: coveredJourneyIds.has(journey.hashId),
      }));
    if (defaultJourney) {
      const defaultLabel = getIntlTranslation(intl, {
        id: 'pages.emailCenter.templates.create.scopeDefault',
        defaultMessage: 'Default — all lead sources',
      });
      options.unshift({
        value: defaultJourney.hashId,
        label: defaultScopeCovered
          ? `${defaultLabel} — ${coveredSuffix}`
          : defaultLabel,
        disabled: defaultScopeCovered,
      });
    }
    return options;
  }, [journeys, coveredJourneyIds, defaultJourney, defaultScopeCovered, intl]);
  /** Default first, then the first lead source the event is still free on. */
  const firstFreeSendTarget = useMemo(
    () => sendTargetOptions.find((option) => !option.disabled)?.value ?? null,
    [sendTargetOptions],
  );
  /**
   * Whether this event is configured per lead source at all — the backend says so
   * per type (`journey_specific`). The calendar event mails are not: the back office
   * schedules an individual meeting for a lead, and the source that lead came from is
   * no reason to send different wording. They exist once for the account, so there is
   * no scope to choose and the picker is replaced by a line saying so.
   */
  const journeySpecific = isJourneySpecificType(templateTypes, presetEmailType);

  /**
   * The journey the create step links the new template to. Journey-independent
   * types always land on the default journey — the one the send actions resolve.
   *
   * The fallback matters more than it looks: an account with **no** default lead
   * source (nothing flagged `is_default`) resolved this to null, the create then
   * sent no `journey_id`, and the API's own fallback is that same missing
   * default — so the template was saved with no journey link and no Mailcoach
   * object behind it. Created, listed, and unable to ever send. The scope bar's
   * lead source is the honest answer there: it is the one on screen.
   */
  const scopeTarget =
    (journeySpecific && LEAD_SOURCE_TEMPLATES_ENABLED
      ? firstFreeSendTarget
      : defaultJourney?.hashId) ??
    scopedJourneyHashId ??
    null;

  /**
   * Preselect the first journey that still lacks this email.
   *
   * Re-derived (rather than set once on open) because on a deep link the
   * template list can arrive after the drawer opened — but only until the user
   * touches the field, since coverage must never override their choice.
   */
  useEffect(() => {
    if (isEdit || !creatingForTrigger || !visible) return;
    if (form.isFieldTouched('journey_hash_id')) return;
    form.setFieldsValue({ journey_hash_id: scopeTarget });
  }, [isEdit, creatingForTrigger, visible, scopeTarget, form]);

  return {
    coveredEventTypes,
    sendTargetOptions,
    journeySpecific,
    scopeTarget,
  };
}

export default useTemplateScopeTarget;
